import { useMemo } from "react";

import { useDebounce } from "@/hooks/useDebounce";

const commonDomains = new Set([
  "gmail.com",
  "ymail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "mail.com",
  "aol.com",
  "kpnmail.nl",
  "ziggo.nl",
  "telfort.nl",
  "hotmail.nl",
  "live.nl",
  "wanadoo.nl",
  "planet.nl",
  "home.nl",
  "casema.nl",
  "hetnet.nl",
  "ziggo.com",
  "gmx.de",
  "gmx.com",
  "web.de",
  "mail.de",
  "email.de",
  "mail.ru",
]);

// Levenshtein distane
function editDistance(a: string, b: string): number {
  if (a.length === 0) {
    return b.length;
  }

  if (b.length === 0) {
    return a.length;
  }

  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const cost = a[j - 1] === b[i - 1] ? 0 : 1;
      let cell = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);

      // Damerau–Levenshtein solution. So transpositions, e.g. gamil.com, are 1 instead of 2 characters off
      if (i > 1 && j > 1 && a[j - 1] === b[i - 2] && a[j - 2] === b[i - 1]) {
        cell = Math.min(cell, matrix[i - 2][j - 2] + 1);
      }

      matrix[i][j] = cell;
    }
  }

  return matrix[b.length][a.length];
}

export enum EmailValidationErrorType {
  // Strict Errors
  LOCAL_STARTS_WITH_WRONG_CHAR,
  LOCAL_ENDS_WITH_DOT,
  LOCAL_CONSECUTIVE_DOTS,
  DOMAIN_STARTS_WITH_DOT,
  DOMAIN_ENDS_WITH_WRONG_CHAR,
  DOMAIN_CONSECUTIVE_DOTS,
  // Typo Errors
  TYPO_TLD_CON,
  TYPO_TLD_CMO,
  TYPO_DOMAIN_EDIT_DISTANCE,
  // General invalid states
  EXCEEDS_MAX_LENGTH,
  MISSING_AT,
  MULTIPLE_AT,
  LOCAL_PART_MISSING,
  LOCAL_PART_EXCEEDS_MAX_LENGTH,
  LOCAL_CONTAINS_MAILTO,
  DOMAIN_PART_MISSING,
  DOMAIN_CONTAINS_UNDERSCORE,
  DOMAIN_TOO_FEW_LABELS,
  DOMAIN_LABEL_INVALID_LENGTH,
  DOMAIN_LABEL_INVALID_HYPHEN,
  INVALID_TLD_FORMAT,
  REGEX_MISMATCH, // Catch-all for format issues not covered by specific rules
}

/**
 * This hook provides a suggested email based on the input email string.
 * It uses a debounce mechanism to not annoy the user with suggestions while typing.
 * But it's also smart that it immediately hides the suggestion if the email becomes valid.
 */
export function useSuggestedEmailFix(email: string): string | undefined {
  const emailError = identifyEmailValidationError(email);
  const noError = !emailError;
  const debouncedEmail = useDebounce(email, 500);
  const emailSuggestionTemp = useMemo(() => {
    const debouncedEmailError = identifyEmailValidationError(debouncedEmail);
    const suggestion = debouncedEmailError
      ? generateSuggestedEmail({ originalEmail: debouncedEmail, error: debouncedEmailError })
      : undefined;

    return suggestion;
  }, [debouncedEmail]);

  return noError ? undefined : emailSuggestionTemp;
}

/**
 * Checks if an email is potentially invalid (also returns true for stricly invalid emails)
 */
export function isMaybeInvalidEmail(email: string): boolean {
  return identifyEmailValidationError(email) !== undefined;
}

/**
 * Checks if an email string is valid according to strict rules and common typo checks.
 */
export function isValidEmail(email: string): boolean {
  const error = identifyEmailValidationError(email);
  if (error === undefined) {
    return true;
  }

  if (
    error === EmailValidationErrorType.TYPO_TLD_CON ||
    error === EmailValidationErrorType.TYPO_TLD_CMO ||
    error === EmailValidationErrorType.TYPO_DOMAIN_EDIT_DISTANCE
  ) {
    // It's technically valid, but we want to suggest a correction
    return true;
  }

  return false;
}

/**
 * Identifies the specific validation error for an email input.
 * Performs strict validation first, then checks for common typos if strictly valid.
 * @param input The email string to validate.
 * @returns An `EmailValidationErrorType` if an error is found, otherwise `undefined`.
 */
export function identifyEmailValidationError(input: string): EmailValidationErrorType | undefined {
  // --- Strict Validation Checks ---
  if (input.length > 254) {
    return EmailValidationErrorType.EXCEEDS_MAX_LENGTH;
  }

  if (!input.includes("@")) {
    return EmailValidationErrorType.MISSING_AT;
  }

  const splitInput = input.split("@");
  if (splitInput.length < 2) {
    return EmailValidationErrorType.DOMAIN_PART_MISSING;
  } else if (splitInput.length === 2) {
    if (splitInput[0] === "") {
      return EmailValidationErrorType.LOCAL_PART_MISSING;
    } else if (splitInput[1] === "") {
      return EmailValidationErrorType.DOMAIN_PART_MISSING;
    }
  } else if (splitInput.length > 2) {
    return EmailValidationErrorType.MULTIPLE_AT;
  }

  const [localPart, domain] = splitInput;

  if (localPart.length > 64) {
    return EmailValidationErrorType.LOCAL_PART_EXCEEDS_MAX_LENGTH;
  }
  if (/^[.:;@\s]/.test(localPart)) {
    return EmailValidationErrorType.LOCAL_STARTS_WITH_WRONG_CHAR;
  }
  if (localPart.endsWith(".")) {
    return EmailValidationErrorType.LOCAL_ENDS_WITH_DOT;
  }
  if (localPart.includes("..")) {
    return EmailValidationErrorType.LOCAL_CONSECUTIVE_DOTS;
  }
  if (localPart.startsWith("mailto:")) {
    return EmailValidationErrorType.LOCAL_CONTAINS_MAILTO;
  }

  if (domain.startsWith(".")) {
    return EmailValidationErrorType.DOMAIN_STARTS_WITH_DOT;
  }
  if (/[.:;\s]$/.test(domain)) {
    return EmailValidationErrorType.DOMAIN_ENDS_WITH_WRONG_CHAR;
  }
  if (domain.includes("..")) {
    return EmailValidationErrorType.DOMAIN_CONSECUTIVE_DOTS;
  }
  if (domain.includes("_")) {
    return EmailValidationErrorType.DOMAIN_CONTAINS_UNDERSCORE;
  }

  const domainLabels = domain.split(".");
  if (domainLabels.length < 2) {
    return EmailValidationErrorType.DOMAIN_TOO_FEW_LABELS;
  }

  for (const domainLabel of domainLabels) {
    if (domainLabel.length < 1 || domainLabel.length > 63) {
      return EmailValidationErrorType.DOMAIN_LABEL_INVALID_LENGTH;
    }
    if (/^[a-zA-Z0-9-]+$/.test(domainLabel)) {
      if (domainLabel.startsWith("-") || domainLabel.endsWith("-")) {
        return EmailValidationErrorType.DOMAIN_LABEL_INVALID_HYPHEN;
      }
    }
  }

  const tld = domainLabels[domainLabels.length - 1];
  if (/^[a-zA-Z0-9-]+$/.test(tld)) {
    if (
      tld.length < 2 ||
      tld.length > 24 ||
      /^\d+$/.test(tld) ||
      (tld.startsWith("xn--") && !/^xn--[a-z0-9]+$/.test(tld))
    ) {
      return EmailValidationErrorType.INVALID_TLD_FORMAT;
    }
  } else if (tld.length < 2) {
    // Non-ASCII TLD (IDN)
    return EmailValidationErrorType.INVALID_TLD_FORMAT;
  }

  const emailRegex = new RegExp(
    "^[\\p{L}\\p{N}!#$%&'*+/=?^_`{|}~.-]+@" +
      "(?:[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]{0,61}[\\p{L}\\p{N}])?\\.)+" +
      "[\\p{L}\\p{N}-]{2,}$",
    "u",
  );

  if (!emailRegex.test(input)) {
    return EmailValidationErrorType.REGEX_MISMATCH;
  }

  // --- Typo Detection Checks (only if strictly valid up to this point) ---
  if (domain.endsWith(".con")) {
    return EmailValidationErrorType.TYPO_TLD_CON;
  }
  if (domain.endsWith(".cmo")) {
    return EmailValidationErrorType.TYPO_TLD_CMO;
  }

  // Only apply edit distance typo detection for ASCII domains
  if (/^[a-zA-Z0-9.-]+$/.test(domain) && !domain.startsWith("xn--") && !commonDomains.has(domain.toLowerCase())) {
    for (const validDomain of commonDomains) {
      const distance = editDistance(domain.toLowerCase(), validDomain);
      if (distance === 1) {
        return EmailValidationErrorType.TYPO_DOMAIN_EDIT_DISTANCE;
      }
    }
  }

  // If all checks passed (strict and typo)
  return undefined;
}

/**
 * Generates a potential valid email suggestion based on the original input and a specific error type.
 * @returns A suggested valid email string, or `undefined` if no valid suggestion can be generated for the error.
 */
export function generateSuggestedEmail({
  originalEmail,
  error,
}: {
  originalEmail: string;
  error: EmailValidationErrorType;
}): string | undefined {
  const [originalLocalPart, originalDomain] = originalEmail.split("@", 2);
  let potentialSuggestion: string | undefined = undefined;

  switch (error) {
    case EmailValidationErrorType.LOCAL_STARTS_WITH_WRONG_CHAR:
      if (originalLocalPart && originalDomain !== undefined) {
        potentialSuggestion = originalLocalPart.substring(1) + "@" + originalDomain;
      }
      break;
    case EmailValidationErrorType.LOCAL_ENDS_WITH_DOT:
      if (originalLocalPart && originalDomain !== undefined) {
        potentialSuggestion = originalLocalPart.slice(0, -1) + "@" + originalDomain;
      }
      break;
    case EmailValidationErrorType.LOCAL_CONTAINS_MAILTO:
      if (originalLocalPart && originalDomain !== undefined) {
        potentialSuggestion = originalLocalPart.replace(/^mailto:/, "") + "@" + originalDomain;
      }
      break;
    case EmailValidationErrorType.LOCAL_CONSECUTIVE_DOTS:
      if (originalLocalPart && originalDomain !== undefined) {
        potentialSuggestion = originalLocalPart.replace(/\.\.+/g, ".") + "@" + originalDomain;
      }
      break;
    case EmailValidationErrorType.DOMAIN_STARTS_WITH_DOT:
      if (originalLocalPart !== undefined && originalDomain) {
        potentialSuggestion = originalLocalPart + "@" + originalDomain.substring(1);
      }
      break;
    case EmailValidationErrorType.DOMAIN_ENDS_WITH_WRONG_CHAR:
      if (originalLocalPart !== undefined && originalDomain) {
        potentialSuggestion = originalLocalPart + "@" + originalDomain.slice(0, -1);
      }
      break;
    case EmailValidationErrorType.DOMAIN_CONSECUTIVE_DOTS:
      if (originalLocalPart !== undefined && originalDomain) {
        potentialSuggestion = originalLocalPart + "@" + originalDomain.replace(/\.\.+/g, ".");
      }
      break;
    case EmailValidationErrorType.TYPO_TLD_CON:
      if (originalLocalPart !== undefined && originalDomain?.endsWith(".con")) {
        potentialSuggestion = `${originalLocalPart}@${originalDomain.slice(0, -3)}com`;
      }
      break;
    case EmailValidationErrorType.TYPO_TLD_CMO:
      if (originalLocalPart !== undefined && originalDomain?.endsWith(".cmo")) {
        potentialSuggestion = `${originalLocalPart}@${originalDomain.slice(0, -3)}com`;
      }
      break;
    case EmailValidationErrorType.TYPO_DOMAIN_EDIT_DISTANCE:
      if (
        originalLocalPart !== undefined &&
        originalDomain &&
        /^[a-zA-Z0-9.-]+$/.test(originalDomain) &&
        !originalDomain.startsWith("xn--") &&
        !commonDomains.has(originalDomain.toLowerCase())
      ) {
        for (const validDomain of commonDomains) {
          const distance = editDistance(originalDomain.toLowerCase(), validDomain);
          if (distance === 1) {
            const tempSuggestion = `${originalLocalPart}@${validDomain}`;
            if (isValidEmail(tempSuggestion)) {
              potentialSuggestion = tempSuggestion;
              break;
            }
          }
        }
      }
      break;
    default:
      return undefined;
  }

  if (potentialSuggestion && isValidEmail(potentialSuggestion)) {
    return potentialSuggestion;
  }

  return undefined;
}
